import { Eye, Pencil, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { studioApi, usePublishReport, useStudioReports, useStudioTeam } from '@/api/studio';
import type { Project, Report } from '@/api/types';
import { ReportArticle } from '@/components/ReportArticle';
import { RowsInput } from '@/components/RowsInput';
import { Dialog } from '@/components/ui';
import { filled } from '@/lib/rows';
import { formatWhen } from '@/lib/format';

type NumberRow = { label: string; value: string; change: string };
const blank: NumberRow = { label: '', value: '', change: '' };

function NumbersInput({ rows, onChange }: { rows: NumberRow[]; onChange: (r: NumberRow[]) => void }) {
  const list = rows.length ? rows : [blank];
  const set = (i: number, patch: Partial<NumberRow>) => onChange(list.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <fieldset className="space-y-1.5">
      <legend className="mb-1.5 text-sm font-semibold">Показатели</legend>
      <p className="text-xs text-muted">Показатели последнего отчёта клиент видит на странице проекта в блоке «Результаты»; одинаковые названия из прошлых отчётов складываются в график. Число лучше писать без лишнего: «7», «3 900».</p>
      {list.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_110px_110px_auto] gap-1.5">
          <input className="field min-w-0" aria-label={`Название ${i + 1}`} placeholder="Название, например: Заявки" value={r.label} onChange={(e) => set(i, { label: e.target.value })} />
          <input className="field num min-w-0" aria-label={`Значение ${i + 1}`} placeholder="Значение" value={r.value} onChange={(e) => set(i, { value: e.target.value })} />
          <input className="field min-w-0" aria-label={`Изменение ${i + 1}`} placeholder="Изменение" value={r.change} onChange={(e) => set(i, { change: e.target.value })} />
          <button type="button" className="btn-ghost size-8 px-0" aria-label={`Удалить: Показатель ${i + 1}`} onClick={() => onChange(list.length === 1 ? [blank] : list.filter((_, j) => j !== i))}>
            <X className="size-4" />
          </button>
        </div>
      ))}
      <button type="button" className="btn-ghost h-7 gap-1 px-2 text-xs" onClick={() => onChange([...list, blank])}>
        <Plus className="size-3.5" /> Добавить
      </button>
    </fieldset>
  );
}

/** Publish a project's report; a published one opens in the same form, and the same period updates it. */
export function StudioReports({ p }: { p: Project }) {
  const { data: team } = useStudioTeam();
  const { data: reports } = useStudioReports(p.id);
  const publish = usePublishReport();
  const [period, setPeriod] = useState('');
  const [summary, setSummary] = useState('');
  const [numbers, setNumbers] = useState<NumberRow[]>([blank]);
  const [done, setDone] = useState<string[]>(['']);
  const [next, setNext] = useState<string[]>(['']);
  const [viewing, setViewing] = useState<Report>();

  const reset = () => (setPeriod(''), setSummary(''), setNumbers([blank]), setDone(['']), setNext(['']));
  const load = (r: Report) => {
    setPeriod(r.period);
    setSummary(r.summary);
    setNumbers(r.numbers.length ? r.numbers.map((n) => ({ label: n.label, value: n.value, change: n.change ?? '' })) : [blank]);
    setDone(r.done.length ? r.done : ['']);
    setNext(r.next.length ? r.next : ['']);
  };
  const existing = reports?.find((r) => r.period === period.trim());
  const isDraft = existing?.status === 'draft';

  return (
    <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_320px]">
      <section>
        <h2 className="mb-3 text-sm font-semibold">{isDraft ? 'Черновик отчёта' : existing ? 'Изменить отчёт' : 'Опубликовать отчёт'}</h2>
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!period.trim() || !summary.trim() || !team) return;
            publish.mutate(
              {
                projectId: p.id,
                period: period.trim(),
                summary: summary.trim(),
                numbers: numbers
                  .filter((n) => n.label.trim())
                  .map((n) => ({ label: n.label.trim(), value: n.value.trim(), ...(n.change.trim() ? { change: n.change.trim() } : {}) })),
                done: filled(done),
                next: filled(next),
                authorId: team[0].id,
              },
              { onSuccess: reset },
            );
          }}
        >
          <div className="space-y-2">
            <input className="field" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="Период, например: 15–21 сентября" />
            <textarea className="field min-h-16" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Главный вывод периода — одна-две фразы для клиента" />
            {isDraft && <p className="text-xs text-muted">Черновик собран конвейером отчётов, клиент его не видит. Проверьте и опубликуйте — клиенту придёт уведомление.</p>}
            {existing && !isDraft && <p className="text-xs text-muted">Отчёт за этот период уже опубликован: публикация обновит его, клиенту второе уведомление не придёт.</p>}
          </div>
          <NumbersInput rows={numbers} onChange={setNumbers} />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <RowsInput label="Сделали" values={done} onChange={setDone} placeholder="Что сделали" />
            <RowsInput label="Дальше" values={next} onChange={setNext} placeholder="Что дальше" />
          </div>
          <div className="flex items-center gap-2">
            <button className="btn-primary" disabled={!period.trim() || !summary.trim() || publish.isPending}>
              Опубликовать клиенту
            </button>
            {(existing || period || summary) && (
              <button type="button" className="btn-ghost" onClick={reset}>
                Очистить форму
              </button>
            )}
          </div>
        </form>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold">Отчёты</h3>
        <p className="mb-2 text-xs text-muted">Откройте отчёт, чтобы посмотреть его так, как видит клиент, или поправить в форме.</p>
        <ul className="space-y-1 text-sm">
          {reports?.map((r) => (
            <li key={r.id}>
              <div className="flex items-center gap-1 rounded px-2 py-1 hover:bg-panel" aria-current={r.period === period.trim() || undefined}>
                <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setViewing(r)}>
                  {r.period}{' '}
                  {r.status === 'draft' ? (
                    <span className="text-muted">черновик{r.draftedAt ? `, ${formatWhen(r.draftedAt)}` : ''}</span>
                  ) : (
                    <span className="text-muted">{r.publishedAt ? formatWhen(r.publishedAt) : ''}, {r.author.name}</span>
                  )}
                </button>
                <button type="button" className="btn-ghost size-7 px-0" aria-label={`Посмотреть отчёт ${r.period}`} title="Посмотреть" onClick={() => setViewing(r)}>
                  <Eye className="size-4" />
                </button>
                <button type="button" className="btn-ghost size-7 px-0" aria-label={`Изменить отчёт ${r.period}`} title="Изменить" onClick={() => load(r)}>
                  <Pencil className="size-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
      <Dialog open={!!viewing} onClose={() => setViewing(undefined)} title={viewing ? `Отчёт: ${viewing.period}` : ''} wide>
        {viewing && (
          <div className="space-y-4 p-5">
            <ReportArticle report={viewing} download={(id) => studioApi.downloadDocument(id)} downloadPdf={() => studioApi.downloadReportPdf(viewing.id)} />
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                load(viewing);
                setViewing(undefined);
              }}
            >
              <Pencil className="size-4" /> Изменить в форме
            </button>
          </div>
        )}
      </Dialog>
    </div>
  );
}
